import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError, tap, finalize } from 'rxjs/operators';

import { User } from '../models/user.model';
import { environment } from 'src/environments/environment';

@Injectable({providedIn: 'root'})
export class ApiService {
  public HEADERS: any;
  public BACKEND_URL: string;
  public headerContentType: string;

  constructor(
    private http: HttpClient
  ) {
    this.headerContentType = 'application/json';
    this.setupHeaders();
    this.BACKEND_URL = environment.BACKEND_URL;
  }

  private setupHeaders() {
    const token = localStorage.getItem('userToken');
    let authHeader = '';
    
    if (token) {
      try {
        const parsedToken = JSON.parse(token);
        if (parsedToken && parsedToken.token) {
          authHeader = `Bearer ${parsedToken.token}`;
        }
      } catch (error) {
        console.error('Error parsing token from localStorage:', error);
      }
    }
    
    this.HEADERS = new HttpHeaders({
      'Content-Type': this.headerContentType,
      'Authorization': authHeader,
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Accept': 'application/json'
    });
  }

  private handleError(error: HttpErrorResponse) {
    console.error('API Error Details:', {
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      error: error.error,
      message: error.message
    });

    if (error.status === 0) {
      console.error('Network error detected - Possible CORS or connectivity issue');
      return throwError('Erro de conexão com o servidor. Por favor, verifique sua conexão e tente novamente.');
    }

    if (error.status === 401) {
      console.error('Unauthorized access - Token may be invalid or expired');
      localStorage.removeItem('token');
      return throwError('Sessão expirada. Por favor, faça login novamente.');
    }

    if (error.status === 403) {
      console.error('Forbidden access');
      return throwError('Acesso não autorizado.');
    }

    if (error.status === 502 || error.status === 504) {
      console.error('Gateway error detected');
      return throwError('Erro de comunicação com o servidor. Por favor, tente novamente em alguns instantes.');
    }

    return throwError(error.error?.message || 'Ocorreu um erro. Por favor, tente novamente.');
  }

  async login(object: User) {
    // Logs apenas em desenvolvimento
    if (!environment.production) {
      console.log('Tentando login com:', { email: object.email, password: '***' });
      console.log('URL da API:', this.BACKEND_URL + '/login/admin');
      console.log('Headers:', this.HEADERS);
    }
    
    return await this.http.post<any>(
      this.BACKEND_URL + '/login/admin',
      object,
      { 
        headers: this.HEADERS,
        withCredentials: environment.production
      }
    ).pipe(
      retry(3),
      catchError((error: HttpErrorResponse) => {
        // Logs detalhados apenas em desenvolvimento
        if (!environment.production) {
          console.error('Erro detalhado do login:', {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            error: error.error,
            message: error.message,
            headers: error.headers
          });
        }
        
        if (error.status === 401) {
          if (!environment.production) {
            console.error('Credenciais inválidas ou usuário não encontrado');
          }
          // Captura a mensagem específica da API
          const apiMessage = error.error?.message || 'Credenciais inválidas';
          return throwError(apiMessage);
        }
        
        return this.handleError(error);
      })
    ).toPromise();
  }

  refreshToken(object: any) {
    return this.http.post<any>(
      `${this.BACKEND_URL}/token/refresh`,
      object,
      { 
        headers: this.HEADERS,
        withCredentials: environment.production
      }
    ).pipe(
      retry(3),
      catchError(this.handleError)
    );
  }

  async apiGet(url: string, options?: string) {
    this.setupHeaders();
    const fullUrl = options ? 
      `${this.BACKEND_URL}${url}?${options}` : 
      `${this.BACKEND_URL}${url}`;

    
    return await this.http.get(fullUrl, {
      headers: this.HEADERS,
      withCredentials: environment.production
    }).pipe(
      retry(2),
      catchError(this.handleError)
    ).toPromise();
  }

  apiGetObservable(url: string, options?: string): Observable<any> {
    this.setupHeaders();
    const fullUrl = options ? 
      `${this.BACKEND_URL}${url}?${options}` : 
      `${this.BACKEND_URL}${url}`;

    return this.http.get<any>(fullUrl, {
      headers: this.HEADERS,
      withCredentials: environment.production
    }).pipe(
      retry(3),
      catchError(this.handleError)
    );
  }

  apiPost(url: string, object: any) {
    this.setupHeaders();
    const fullUrl = `${this.BACKEND_URL}${url}`;

    return this.http.post(fullUrl, object, {
      headers: this.HEADERS,
      withCredentials: environment.production
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  apiPut(url: string, object: any) {
    this.setupHeaders();
    const fullUrl = `${this.BACKEND_URL}${url}`;

    return this.http.put(fullUrl, object, {
      headers: this.HEADERS,
      withCredentials: environment.production
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  apiDelete(url: string, id: string) {
    this.setupHeaders();
    const fullUrl = `${this.BACKEND_URL}${url}/${id}`;
    
    return this.http.delete(fullUrl, {
      headers: this.HEADERS,
      withCredentials: environment.production
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }
}
